/* ============================================================
   SINGSPACE ADMIN - CRÉATION ESPACE (Refactorisée)
   ============================================================ */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../../services/api';
import { Sparkles } from 'lucide-react';
import { SpaceCreateHeader } from '../../components/spaces/SpaceCreateHeader';
import { WorkflowSelector } from '../../components/spaces/wizard/WorkflowSelector';
import { generateSlug } from '../../utils/space.utils';
import { SpaceFormData } from '../../types/space.types';

export function SpaceCreatePage() {
  const navigate = useNavigate();
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<SpaceFormData>({
    name: '', slug: '', shortDesc: '', description: '', workflow: 'IMMEDIATE',
  });

  const handleFieldChange = (field: keyof SpaceFormData, value: string) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };
      if (field === 'name') updated.slug = generateSlug(value);
      return updated;
    });
  };

  const handleCreate = async () => {
    if (!formData.name || !formData.shortDesc || !formData.description) {
      setError('Veuillez remplir tous les champs obligatoires');
      return;
    }
    setIsSaving(true);
    setError(null);
    try {
      const res = await apiService.createSpace({ ...formData, category: 'GENERAL', isActive: false, isPublic: false, order: 0, colorPrimary: '#00758d', colorAccent: '#8E0B56' });
      const newId = res.data.data?.id;
      navigate(newId ? `/spaces/${newId}/edit` : '/spaces');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erreur lors de la création');
    } finally { setIsSaving(false); }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafb' }}>
      <SpaceCreateHeader />
      <div style={{ padding: '3rem 2rem' }}>
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>
          <div style={{ background: 'white', borderRadius: '16px', boxShadow: '0 4px 24px rgba(0,48,60,0.08)', padding: '3rem' }}>
            {error && <ErrorMessage message={error} />}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
              <InputField
                label="Nom de l'espace"
                value={formData.name}
                onChange={(v: string) => handleFieldChange('name', v)}
                placeholder="Ex: Salle de Conférence"
                required
              />
              <InputField label="Slug (URL)" value={formData.slug} readOnly placeholder="salle-conference" required />
              <InputField
                label="Description courte"
                value={formData.shortDesc}
                onChange={(v: string) => handleFieldChange('shortDesc', v.slice(0, 50))}
                placeholder="Max 50 caractères"
                maxLength={50}
                required
              />
              <div>
                <label style={labelStyle}>Description complète *</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => handleFieldChange('description', e.target.value)}
                  placeholder="Détails..."
                  rows={6}
                  style={inputStyle}
                />
              </div>
              <WorkflowSelector
                currentWorkflow={formData.workflow}
                onSelect={(v) => handleFieldChange('workflow', v as any)}
              />
            </div>
            <FormActions
              onCancel={() => navigate('/spaces')}
              onCreate={handleCreate}
              isSaving={isSaving}
              isValid={!!formData.name && !!formData.shortDesc && !!formData.description}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

const labelStyle: React.CSSProperties = {
  display: 'block',
  marginBottom: '0.5rem',
  fontSize: '0.9rem',
  fontWeight: 600,
  color: '#00303c',
};
const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '0.875rem 1rem',
  fontSize: '1rem',
  border: '2px solid #e5e7eb',
  borderRadius: '12px',
  outline: 'none',
};

function ErrorMessage({ message }: { message: string }) {
  return (
    <div
      style={{
        padding: '1rem',
        background: '#fff5f5',
        border: '1px solid #fecaca',
        borderRadius: '12px',
        color: '#8E0B56',
        marginBottom: '2rem',
      }}
    >
      {message}
    </div>
  );
}

interface InputFieldProps {
  label: string;
  value: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  required?: boolean;
  readOnly?: boolean;
  maxLength?: number;
}

function InputField({ label, value, onChange, placeholder, required, readOnly, maxLength }: InputFieldProps) {
  return (
    <div>
      <label style={labelStyle}>
        {label} {required && '*'}
      </label>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange?.(e.target.value)}
        placeholder={placeholder}
        readOnly={readOnly}
        maxLength={maxLength}
        style={{ ...inputStyle, background: readOnly ? '#f8fafb' : 'white' }}
      />
    </div>
  );
}

interface FormActionsProps {
  onCancel: () => void;
  onCreate: () => void;
  isSaving: boolean;
  isValid: boolean;
}

function FormActions({ onCancel, onCreate, isSaving, isValid }: FormActionsProps) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '1rem',
        paddingTop: '2rem',
        marginTop: '2rem',
        borderTop: '2px solid #f0f0f0',
      }}
    >
      <button
        onClick={onCancel}
        style={{
          padding: '0.75rem 1.5rem',
          background: 'transparent',
          color: '#666',
          border: '2px solid #e5e7eb',
          borderRadius: '12px',
          fontWeight: 600,
          cursor: 'pointer',
        }}
      >
        Annuler
      </button>
      <button
        onClick={onCreate}
        disabled={!isValid || isSaving}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          padding: '0.75rem 1.5rem',
          background: isValid && !isSaving ? '#00758d' : '#e5e7eb',
          color: 'white',
          border: 'none',
          borderRadius: '12px',
          fontWeight: 600,
          cursor: isValid && !isSaving ? 'pointer' : 'not-allowed',
        }}
      >
        {isSaving ? 'Création...' : 'Créer et configurer'} {!isSaving && <Sparkles size={18} />}
      </button>
    </div>
  );
}
